import React, { useMemo, useState } from 'react'
import type { FC } from 'react'
import { Link as ReactRouterLink } from 'react-router'
import { useSiblingTabPath } from '~/shared/hooks/useRouteBasePath'
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Link,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import DoneIcon from '@mui/icons-material/Done'
import ArrowRight from '@mui/icons-material/ArrowRight'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import AltRouteIcon from '@mui/icons-material/AltRoute'
import { format, parseISO } from 'date-fns'
import { Person } from '~/shared/types'
import { EmptyState, Loader, SectionCard, StatusPill, TABLE_HEADER_CELL_SX } from '~/shared/ui'
import { useSteerages } from '~/entities/steerage'
import {
  getCreatedDate,
  getFormattedCreatedDate,
  getFilteredSteerageProvider,
  getSteerageType,
  showProviderNameColumnData,
  type SteerageProviderItem,
} from './steerageDisplay'

interface NavigationListProps {
  person: Person
}

/**
 * Design-refresh Navigation tab (shared by patient and person modes): the
 * legacy steerage list's data, sorting, provider popover, and expandable
 * cases verbatim, re-rendered as a SectionCard table with toned status pills.
 * The status pills render inside the description cell rather than a separate
 * table row, keeping each steerage to a single row.
 */
const NavigationList: FC<NavigationListProps> = props => {
  const todosPath = useSiblingTabPath('todos')
  const { data: steerages, isPending: isLoading } = useSteerages(props.person.id)
  const [sortDesc, setSortDesc] = useState(true)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedProviderPopOverData, setSelectedProviderPopOverData] =
    useState<Array<SteerageProviderItem> | null>(null)
  const [open, setOpen] = useState<number | null>(null)

  const handleProviderPopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    providers: Array<SteerageProviderItem> | null,
  ) => {
    setAnchorEl(event.currentTarget)
    setSelectedProviderPopOverData(providers)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
    setSelectedProviderPopOverData(null)
  }

  const sortedSteerages = useMemo(() => {
    return (steerages ?? []).sort((steerageA, steerageB) => {
      const createdDateA = getCreatedDate(steerageA)
      const createdDateB = getCreatedDate(steerageB)
      if (!createdDateA || !createdDateB) return 0
      if (sortDesc) return createdDateB.getTime() - createdDateA.getTime()
      else return createdDateA.getTime() - createdDateB.getTime()
    })
  }, [steerages, sortDesc])

  const schedulingDates = useMemo(() => {
    const dates: Record<string, string | null> = {}
    for (const steerage of steerages ?? []) {
      if (steerage.scheduling_date) {
        const formatted = format(parseISO(steerage.scheduling_date), 'MMM dd, yyyy')
        dates[steerage.id] = 'Scheduled ' + formatted
      } else {
        dates[steerage.id] = null
      }
    }
    return dates
  }, [steerages])

  if (isLoading) return <Loader />

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: '1.25rem', color: 'text.secondary' }}>
          {sortedSteerages.length} {sortedSteerages.length === 1 ? 'record' : 'records'}
        </Typography>
        <Tooltip title="Sort by created date" placement="top">
          <IconButton
            size="small"
            aria-label="Sort by created date"
            onClick={() => {
              setSortDesc(s => !s)
            }}
          >
            {sortDesc ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Pop over for the provider list */}
      <Popover
        style={{ pointerEvents: 'none' }}
        open={selectedProviderPopOverData && selectedProviderPopOverData.length > 0 ? true : false}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        {selectedProviderPopOverData?.map(provider => (
          <Box key={provider?.id} sx={{ px: 2, py: 1 }}>
            <Typography variant="caption">
              {provider?.care_organization_name
                ? provider?.care_organization_name
                : provider?.first_name + ' ' + provider?.last_name}{' '}
              {provider?.id ? `(ID: ${provider.id})` : null}{' '}
              {provider.member_selected_at ? (
                <DoneIcon fontSize="small" style={{ verticalAlign: 'bottom' }} />
              ) : null}
            </Typography>
          </Box>
        ))}
      </Popover>

      {sortedSteerages.length === 0 ? (
        <EmptyState icon={<AltRouteIcon />} title="No referrals or care keys" />
      ) : (
        <SectionCard disablePadding>
          <Table aria-label="Network navigation" sx={{ 'td, th': { borderColor: 'divider' } }}>
            <TableHead>
              <TableRow>
                <TableCell width="30%" sx={TABLE_HEADER_CELL_SX}>
                  Description
                </TableCell>
                <TableCell width="30%" sx={TABLE_HEADER_CELL_SX}>
                  Providers
                </TableCell>
                <TableCell width="20%" sx={TABLE_HEADER_CELL_SX}>
                  Created Date
                </TableCell>
                <TableCell width="20%" sx={TABLE_HEADER_CELL_SX}>
                  Cases
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSteerages.map((steerage, index) => {
                const isLast = index === sortedSteerages.length - 1
                const cellSx = { borderBottom: isLast ? 0 : undefined, fontSize: '1.3rem' }
                return (
                  <React.Fragment key={steerage.id}>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ ...cellSx, borderBottom: 0 }}>
                        <Typography sx={{ fontSize: '1.35rem', fontWeight: 500 }}>
                          {/* Show 'None' when description is null */}
                          {steerage.description ?? 'None'} {' - '} {getSteerageType(steerage)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                          <StatusPill tone="neutral" label={steerage.status} />
                          {schedulingDates[steerage.id] ? (
                            <StatusPill tone="info" label={schedulingDates[steerage.id]} />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell align="left" sx={{ ...cellSx, borderBottom: 0 }}>
                        {steerage && getFilteredSteerageProvider(steerage)?.length > 0 && (
                          <Box
                            onMouseEnter={event =>
                              handleProviderPopoverOpen(
                                event,
                                getFilteredSteerageProvider(steerage) ?? null,
                              )
                            }
                            onMouseLeave={handlePopoverClose}
                          >
                            {showProviderNameColumnData(getFilteredSteerageProvider(steerage))}{' '}
                            {getFilteredSteerageProvider(steerage)?.length > 1 ? '...' : ''}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="left" sx={{ ...cellSx, borderBottom: 0 }}>
                        {getFormattedCreatedDate(steerage)}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, borderBottom: 0 }}>
                        <Button
                          onClick={() => setOpen(open == steerage?.id ? null : steerage?.id)}
                          startIcon={open == steerage?.id ? <ArrowDropDown /> : <ArrowRight />}
                        >
                          {steerage?.cases?.length + ' case(s)'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={4}
                        sx={{ borderBottom: isLast ? 0 : undefined }}
                      >
                        <Collapse in={open == steerage?.id} timeout="auto" unmountOnExit>
                          <Box sx={{ pb: 1.5, pl: 1 }}>
                            {steerage?.cases?.map((steerageCase, caseIndex) => (
                              <Box key={steerageCase.id}>
                                {caseIndex > 0 && <Divider sx={{ my: 1 }} />}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Link
                                    component={ReactRouterLink}
                                    to={`${todosPath}?caseId=${steerageCase.id}`}
                                    sx={{ fontSize: '1.3rem' }}
                                  >
                                    {steerageCase?.category?.title}
                                  </Link>
                                  {steerageCase?.status ? (
                                    <StatusPill tone="neutral" label={steerageCase.status} />
                                  ) : null}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </SectionCard>
      )}
    </Box>
  )
}

export default NavigationList
